import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import amex from '../../resources/images/CreditCardLogos/amex.svg';
import discover from '../../resources/images/CreditCardLogos/discover.svg';
import visa from '../../resources/images/CreditCardLogos/visa.svg';
import mastercard from '../../resources/images/CreditCardLogos/mastercard.svg';
import { RedButton } from '../../components/Button';
import { CreditCardInfo } from '../../interfaces/CreditCardInfo';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { ConfigContext } from '../../providers/ConfigContext';
import getCybersourceToken from '../../utils/getCbsSignature';
import getCbsPayoffSignature from '../../utils/getCbsPayoffSignature';
import { CybersourceSignature, defaultCbsSign } from '../../interfaces/cybersource-signature';
import getRecaptcha from '../../utils/getRecaptcha';
import bookfair from '../../interfaces/bookfair-info';
import updateSessionInfo from '../../utils/updateSessionInfo';
import { FairBarInfo } from '../../interfaces/fairBar-info';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import FairInfoBar from '../../components/FairInfoBar';
import { Pages } from '../../App';
import { setTaxErrorData } from '../../DumbleData';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';

interface Props {
	ccInfo: CreditCardInfo;
	pInfo: PaymentInfo;
	bfInfo: bookfair;
	setBookfairInfo: any;
	setTransactionId: any;
	isTaxExemptChecked: boolean;
	version: string;
	payoffAmount: BigNumber;
	payoffFairId: number;
	payoffFairInfo: FairBarInfo;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [getPayoffToken] = getCbsPayoffSignature();
const [getToken] = getCybersourceToken();
const [getCaptcha] = getRecaptcha();
const { getSessionInfo } = updateSessionInfo();

const getCreditCardType = (cardType: string) => {
	switch (cardType) {
		case 'American Express':
			return amex;
		case 'Discover':
			return discover;
		case 'Visa':
			return visa;
		default:
			return mastercard;
	}
};

const getFormattedDate = (month: string, year: string) => {
	return `${month.toString().padStart(2, '0')}-${year}`;
};

const getLastDigits = (cardNumber: string, cardType: string) => {
	return cardType === 'American Express' ? cardNumber.slice(-5) : cardNumber.slice(-4);
};

const handleTaxDiscrepancy = async (apiBase: string, setBookfairInfo: any, setCurrentPage: React.Dispatch<React.SetStateAction<number>>) => {
	const success = await getSessionInfo(apiBase, setBookfairInfo);
	if (success) {
		setCurrentPage(Pages.NewPurchase);
	} else {
		setCurrentPage(Pages.Landing);
	}
};

const requestCbsSignPayoff = async (payoffAmount: number, firstName: string, lastName: string, callGetSignPayoff: (payload: any) => Promise<void>) => {
	const cbsSignature = {
		amount: payoffAmount,
		billToForename: firstName,
		billToSurname: lastName,
	};
	await callGetSignPayoff(cbsSignature);
};

const requestCbsSignSale = async (
	pInfo: { amountDue: number; tax: number; itemTotal: number },
	ccInfo: { firstName: string; lastName: string },
	bfInfo: { taxRate: number; taxStatus: string },
	isTaxExemptChecked: boolean,
	callGetSign: (payload: any) => Promise<void>
) => {
	const cbsSignature = {
		amount: pInfo.amountDue,
		billToForename: ccInfo.firstName,
		billToSurname: ccInfo.lastName,
		tax: {
			amount: pInfo.tax,
			rate: bfInfo.taxRate,
			taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal,
			status: bfInfo.taxStatus,
			selectedExempt: isTaxExemptChecked,
		},
	};
	await callGetSign(cbsSignature);
};

const CreditCardFinal = ({
	ccInfo,
	pInfo,
	bfInfo,
	setBookfairInfo,
	setTransactionId,
	isTaxExemptChecked,
	version,
	payoffAmount,
	payoffFairId,
	setSaleResponseInfo,
	setCurrentPage,
	payoffFairInfo,
}: Props) => {
	const { apiBase, cbsUrl, reCaptchaSiteKey, reCaptchaCheckboxKey } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [loading, setLoading] = useState(true);
	const [cbsSign, setCbsSign] = useState<CybersourceSignature>(defaultCbsSign);
	const [checkboxActive, setCheckboxActive] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);

	useEffect(() => {
		const callGetSign = async (payload: any) => {
			const { data, error } = await getToken(apiBase, payload, 'sale');
			if (error) {
				if (error.states === 401) {
					setCurrentPage(Pages.Landing);
				}
				setStatus(error.status);
			} else if (data != null) {
				setCbsSign(data);
				setLoading(false);
			}
		};

		const callGetSignPayoff = async (payload: any) => {
			const { data, error } = await getPayoffToken(apiBase, payload, payoffFairId);
			if (error) {
				if (error.states === 401) {
					setCurrentPage(Pages.Landing);
				}
				setStatus(error.status);
			} else if (data != null) {
				setLoading(false);
				setCbsSign(data);
			}
		};

		if (version === 'sale') {
			requestCbsSignSale(
				{ amountDue: pInfo.amountDue.toNumber(), tax: pInfo.tax.toNumber(), itemTotal: pInfo.itemTotal.toNumber() },
				{ firstName: ccInfo.firstName, lastName: ccInfo.lastName },
				{ taxRate: bfInfo.taxRate.toNumber(), taxStatus: bfInfo.taxStatus },
				isTaxExemptChecked,
				callGetSign
			);
		} else if (version === 'payoff') {
			requestCbsSignPayoff(payoffAmount.toNumber(), ccInfo.firstName, ccInfo.lastName, callGetSignPayoff);
		}
	}, [
		bfInfo.taxRate,
		bfInfo.taxStatus,
		ccInfo.firstName,
		ccInfo.lastName,
		isTaxExemptChecked,
		pInfo.amountDue,
		pInfo.itemTotal,
		pInfo.tax,
		payoffAmount,
		version,
		payoffFairId,
		apiBase,
		setCurrentPage,
	]);

	const handlePayment = (e: any) => {
		e.preventDefault();
		setLoading(true);
		window.grecaptcha.enterprise.ready(async () => {
			const token = await window.grecaptcha.enterprise.execute(reCaptchaSiteKey, { action: 'CHECKOUT' });
			const { data } = await getCaptcha(token, apiBase, false);
			if (!data) {
				return;
			}
			if (data.status.toLowerCase() !== 'success') {
				setCheckboxActive(true);
				return;
			}
			if (!formRef.current) {
				return;
			}
			formRef.current.submit();
		});
	};

	(window as any).processRequest = (statusCode: any) => {
		if (statusCode && statusCode === 200) {
			setSaleResponseInfo({
				parentTransactionId: cbsSign.merchantDefinedData12 || '0',
				amount: pInfo.amountDue.toNumber(),
				tenders: {
					credit: pInfo.amountDue.toNumber(),
				},
			});
			setTransactionId(cbsSign.merchantDefinedData12);
			window._satellite.track('credit-card-submit');
			setCurrentPage(Pages.Confirmation);
		} else {
			setStatus(statusCode);
		}
	};

	useEffect(() => {
		try {
			window.grecaptcha.enterprise.render('recaptchaContainer', {
				sitekey: reCaptchaCheckboxKey,
				action: 'CHECKOUT',
				callback: checkboxCallback,
			});
		} catch (error) {
			// reCAPTCHA may throw an error if the container has already been rendered
		}
	}, []);

	const checkboxCallback = async (token: string) => {
		const { data } = await getCaptcha(token, apiBase, true);
		if (!data || data.status.toLowerCase() !== 'success' || !formRef.current) {
			return;
		}
		formRef.current.submit();
	};

	const renderForm = () => {
		return (
			<div style={{ display: 'none' }}>
				<iframe name="cc-ui" id="cc-ui" title="cc ui"></iframe>
				<form ref={formRef} action={cbsUrl} method="POST" target="cc-ui">
					<input type="text" name="access_key" id={cbsSign.accessKey} value={cbsSign.accessKey} readOnly hidden></input>
					<input type="text" name="amount" id={cbsSign.amount} value={cbsSign.amount} readOnly hidden></input>
					<input type="text" name="bill_to_address_country" id={cbsSign.billToAddressCountry} value={cbsSign.billToAddressCountry} readOnly hidden></input>
					<input type="text" name="bill_to_email" id={cbsSign.billToEmail} value={cbsSign.billToEmail} readOnly hidden></input>
					<input type="text" name="bill_to_forename" id={cbsSign.billToForename} value={cbsSign.billToForename} readOnly hidden></input>
					<input type="text" name="bill_to_surname" id={cbsSign.billToSurname} value={cbsSign.billToSurname} readOnly hidden></input>
					<input type="text" name="currency" id={cbsSign.currency} value={cbsSign.currency} readOnly hidden></input>
					<input type="text" name="ignore_avs" id={cbsSign.ignoreAvs} value={cbsSign.ignoreAvs} readOnly hidden></input>
					<input type="text" name="locale" id={cbsSign.locale} value={cbsSign.locale} readOnly hidden></input>
					<input type="text" name="profile_id" id={cbsSign.profileId} value={cbsSign.profileId} readOnly hidden></input>
					<input type="text" name="reference_number" id={cbsSign.referenceNumber} value={cbsSign.referenceNumber} readOnly hidden></input>
					<input type="text" name="signature" id={cbsSign.signature} value={cbsSign.signature} readOnly hidden></input>
					<input type="text" name="signed_date_time" id={cbsSign.signedDateTime} value={cbsSign.signedDateTime} readOnly hidden></input>
					<input type="text" name="signed_field_names" id={cbsSign.signedFieldNames} value={cbsSign.signedFieldNames} readOnly hidden></input>
					<input type="text" name="transaction_type" id={cbsSign.transactionType} value={cbsSign.transactionType} readOnly hidden></input>
					<input type="text" name="transaction_uuid" id={cbsSign.transactionUuid} value={cbsSign.transactionUuid} readOnly hidden></input>
					<input type="text" name="payment_method" id={cbsSign.paymentMethod} value={cbsSign.paymentMethod} readOnly hidden></input>
					<input type="text" name="unsigned_field_names" id={cbsSign.unsignedFieldNames} value={cbsSign.unsignedFieldNames} readOnly hidden></input>
					<input type="text" name="merchant_defined_data5" id={cbsSign.merchantDefinedData5} value={cbsSign.merchantDefinedData5} readOnly hidden></input>
					<input type="text" name="merchant_defined_data6" id={cbsSign.merchantDefinedData6} value={cbsSign.merchantDefinedData6} readOnly hidden></input>
					<input type="text" name="merchant_defined_data7" id={cbsSign.merchantDefinedData7} value={cbsSign.merchantDefinedData7} readOnly hidden></input>
					<input type="text" name="merchant_defined_data8" id={cbsSign.merchantDefinedData8} value={cbsSign.merchantDefinedData8} readOnly hidden></input>
					<input type="text" name="merchant_defined_data9" id={cbsSign.merchantDefinedData9} value={cbsSign.merchantDefinedData9} readOnly hidden></input>
					<input type="text" name="merchant_defined_data10" id={cbsSign.merchantDefinedData10} value={cbsSign.merchantDefinedData10} readOnly hidden></input>
					<input type="text" name="merchant_defined_data11" id={cbsSign.merchantDefinedData11} value={cbsSign.merchantDefinedData11} readOnly hidden></input>
					<input type="text" name="merchant_defined_data12" id={cbsSign.merchantDefinedData12} value={cbsSign.merchantDefinedData12} readOnly hidden></input>
					<input type="text" name="card_cvn" id={ccInfo.cardCvv} value={ccInfo.cardCvv} readOnly hidden></input>
					<input
						type="text"
						name="card_expiry_date"
						id={getFormattedDate(ccInfo.cardExpirationMonth, ccInfo.cardExpirationYear)}
						value={getFormattedDate(ccInfo.cardExpirationMonth, ccInfo.cardExpirationYear)}
						readOnly
						hidden
					></input>
					<input type="text" name="card_number" id={ccInfo.cardNumber} value={ccInfo.cardNumber} readOnly hidden></input>
					<input type="text" name="card_type" id={ccInfo.cbsCT} value={ccInfo.cbsCT} readOnly hidden></input>
				</form>
			</div>
		);
	};

	let errorMessage: React.ReactNode = null;
	if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={() => handleTaxDiscrepancy(apiBase, setBookfairInfo, setCurrentPage)}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your credit card. Please use a different payment method or call 877-245-0903.';
	}

	return (
		<>
			{version === 'payoff' && (
				<>
					<LandingPageHeader />
					<FairInfoBar fairInfo={payoffFairInfo} />
				</>
			)}
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Finalize Your Payment" location={Pages.CreditCardForm} />
			<CreditCardFinalPage>
				<ContentWrapper>
					<RowItemDiv>
						<ItemLabel>Amount</ItemLabel>
						<ItemValue>{formatCurrencyNumber(version === 'payoff' ? payoffAmount.toNumber() : pInfo.amountDue.toNumber())}</ItemValue>
					</RowItemDiv>
					<HorizontalLineBreak />
					<CreditSummaryDiv>
						<StyledImg src={getCreditCardType(ccInfo.cardType)} alt="Credit Card" />
						<CreditSummaryText>
							<b>
								{ccInfo.cardType} ending in {getLastDigits(ccInfo.cardNumber, ccInfo.cardType)}
							</b>{' '}
							<br /> {ccInfo.firstName} {ccInfo.lastName}
						</CreditSummaryText>
					</CreditSummaryDiv>
					<HorizontalLineBreak />
					{renderForm()}
					<RecaptchaContainer $checkboxActive={checkboxActive} className="g-recaptcha" id="recaptchaContainer"></RecaptchaContainer>
					<StyledRedButton disabled={status !== 200 || loading} handleClick={handlePayment}>
						Submit Payment
					</StyledRedButton>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
				</ContentWrapper>
			</CreditCardFinalPage>
		</>
	);
};

const RecaptchaContainer = styled.div<{ $checkboxActive?: boolean }>`
	display: ${(props) => (props.$checkboxActive ? 'block' : 'none')};
`;

const CreditCardFinalPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
`;

const HorizontalLineBreak = styled.hr`
	width: 100%;
	height: 1px;
	background: #d8d8d8;
	border: 0px solid #d8d8d8;
	margin: 0px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	margin: 39px 0 25px 0;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const CreditSummaryDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	padding: 10px 20px 10px 10px;
	box-sizing: border-box;
	justify-content: space-between;
`;

const StyledImg = styled.img`
	width: 50px;
	height: 50px;
	margin-right: 35px;
	left: 0px;
`;

const CreditSummaryText = styled(BodyText)`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	text-align: left;
	margin: 0;
	b {
		font-weight: 600;
	}
`;

const StyledRedButton = styled(RedButton)`
	margin: 40px 0 7px 0;
	width: 100%;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	color: #e81111;
	text-align: left;
	margin-bottom: 20px;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default CreditCardFinal;
